/**
 * Canvas renderer for printed artwork.
 *
 * 1. renderSurface: paints the artwork onto the unwrapped band texture
 *    (px per mm = SURFACE_PX_PER_MM), repeating across the seam.
 * 2. drawCylinder / drawFlat: project that texture into the stage canvas.
 *
 * The texture is exactly what a future WebGL cylinder would sample, and it
 * is also the basis for a production-ready unwrapped proof.
 */
import type { ArtworkPlacement, InkSetup, MaterialId } from '@shared/rfq';
import { ELLIPSE_RATIO, type ViewBox } from './geometry';
import { MATERIAL_LOOK, inkOpacity, type ContainerModel } from './model';

export const SURFACE_PX_PER_MM = 5;

export function renderSurface(
  target: HTMLCanvasElement,
  image: CanvasImageSource | null,
  placement: ArtworkPlacement | null,
  C: number,
  H: number,
): HTMLCanvasElement {
  const k = SURFACE_PX_PER_MM;
  const w = Math.round(C * k);
  const h = Math.round(H * k);
  if (target.width !== w) target.width = w;
  if (target.height !== h) target.height = h;
  const ctx = target.getContext('2d')!;
  ctx.clearRect(0, 0, w, h);
  if (!image || !placement) return target;

  ctx.imageSmoothingQuality = 'high';
  for (const offset of [-C, 0, C]) {
    ctx.save();
    ctx.translate((placement.uMm + offset) * k, placement.vMm * k);
    ctx.rotate((placement.rotationDeg * Math.PI) / 180);
    const dw = placement.widthMm * k;
    const dh = placement.heightMm * k;
    ctx.drawImage(image, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }
  return target;
}

export interface StageCanvasOptions {
  canvas: HTMLCanvasElement;
  viewBox: ViewBox;
  /** CSS pixel size of the stage. */
  cssWidth: number;
  cssHeight: number;
  dpr: number;
}

/** Prepares the stage canvas and returns a context whose units are stage mm. */
export function beginStage({ canvas, viewBox, cssWidth, cssHeight, dpr }: StageCanvasOptions): CanvasRenderingContext2D {
  const pw = Math.max(1, Math.round(cssWidth * dpr));
  const ph = Math.max(1, Math.round(cssHeight * dpr));
  if (canvas.width !== pw) canvas.width = pw;
  if (canvas.height !== ph) canvas.height = ph;
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, pw, ph);
  const s = pw / viewBox.w;
  ctx.setTransform(s, 0, 0, s, -viewBox.x * s, -viewBox.y * s);
  return ctx;
}

interface InkLook {
  material: MaterialId;
  ink: InkSetup;
}

/**
 * Cylindrical projection by vertical strips: for each screen column find the
 * surface u it shows, copy that texture column, shifted down by the ellipse
 * offset of that angle. Then shade for curvature and add varnish sheen.
 */
export function drawCylinder(
  ctx: CanvasRenderingContext2D,
  surface: HTMLCanvasElement,
  c: ContainerModel,
  viewAngleDeg: number,
  look: InkLook,
) {
  const r = c.diameterMm / 2;
  const C = Math.PI * c.diameterMm;
  const H = c.print.heightMm;
  const k = SURFACE_PX_PER_MM;
  const phi = (viewAngleDeg * Math.PI) / 180;
  const scale = ctx.getTransform().a; // device px per mm
  const cols = Math.max(64, Math.round(2 * r * scale));
  const colW = (2 * r) / cols;
  const sw = surface.width;

  // Through transparent glass the print on the far side is visible, mirrored.
  // With a white underbase you see the white layer from inside; without it, the colours.
  const lookM = MATERIAL_LOOK[look.material];
  const seeThrough = lookM.opacity >= 0.9 ? 0 : lookM.frosted ? 0.16 : lookM.base === 'tinted' ? 0.22 : 0.4;
  if (seeThrough > 0) {
    const back = document.createElement('canvas');
    back.width = ctx.canvas.width;
    back.height = ctx.canvas.height;
    const bctx = back.getContext('2d')!;
    bctx.setTransform(ctx.getTransform());
    for (let i = 0; i < cols; i++) {
      const x0 = -r + i * colW;
      const xm = x0 + colW / 2;
      const theta = Math.PI - Math.asin(Math.max(-1, Math.min(1, xm / r)));
      const cos = Math.cos(theta);
      const u = (((theta + phi) * r) % C + C) % C;
      const du = colW / Math.max(0.08, -cos);
      const sx = Math.floor(u * k);
      const swCols = Math.max(1, Math.min(sw - sx, Math.ceil(du * k)));
      const y = c.print.topMm + ELLIPSE_RATIO * r * cos;
      bctx.drawImage(surface, sx, 0, swCols, surface.height, x0, y, colW + 0.02, H);
    }
    if (look.ink !== 'cmyk') {
      bctx.globalCompositeOperation = 'source-in';
      bctx.setTransform(1, 0, 0, 1, 0, 0);
      bctx.fillStyle = '#f2f1ec';
      bctx.fillRect(0, 0, back.width, back.height);
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = seeThrough;
    if (lookM.frosted) ctx.filter = `blur(${Math.max(1, Math.round(scale * 0.8))}px)`;
    ctx.drawImage(back, 0, 0);
    ctx.restore();
  }

  // Draw into an offscreen layer so shading only touches printed pixels.
  const layer = document.createElement('canvas');
  layer.width = ctx.canvas.width;
  layer.height = ctx.canvas.height;
  const lctx = layer.getContext('2d')!;
  lctx.setTransform(ctx.getTransform());
  lctx.imageSmoothingEnabled = true;

  for (let i = 0; i < cols; i++) {
    const x0 = -r + i * colW;
    const xm = x0 + colW / 2;
    const theta = Math.asin(Math.max(-1, Math.min(1, xm / r)));
    const cos = Math.cos(theta);
    const u = (((theta + phi) * r) % C + C) % C;
    // Texture columns this strip covers (arc length grows toward the edges).
    const du = colW / Math.max(0.08, cos);
    const sx = Math.floor(u * k);
    const swCols = Math.max(1, Math.min(sw - sx, Math.ceil(du * k)));
    const y = c.print.topMm + ELLIPSE_RATIO * r * cos;
    lctx.drawImage(surface, sx, 0, swCols, surface.height, x0, y, colW + 0.02, H);
  }

  // Curvature shading and a soft specular stripe, only where ink is.
  lctx.globalCompositeOperation = 'source-atop';
  const grad = lctx.createLinearGradient(-r, 0, r, 0);
  grad.addColorStop(0, 'rgba(0,0,0,0.45)');
  grad.addColorStop(0.18, 'rgba(0,0,0,0.12)');
  grad.addColorStop(0.32, 'rgba(255,255,255,0.10)');
  grad.addColorStop(0.42, 'rgba(0,0,0,0)');
  grad.addColorStop(0.78, 'rgba(0,0,0,0.10)');
  grad.addColorStop(1, 'rgba(0,0,0,0.5)');
  lctx.fillStyle = grad;
  lctx.fillRect(-r, c.print.topMm - 2, 2 * r, H + r * ELLIPSE_RATIO + 4);

  if (look.ink === 'cmyk_white_varnish') {
    const sheen = lctx.createLinearGradient(-r, 0, r, 0);
    sheen.addColorStop(0.22, 'rgba(255,255,255,0)');
    sheen.addColorStop(0.3, 'rgba(255,255,255,0.32)');
    sheen.addColorStop(0.36, 'rgba(255,255,255,0)');
    lctx.fillStyle = sheen;
    lctx.fillRect(-r, c.print.topMm - 2, 2 * r, H + r * ELLIPSE_RATIO + 4);
  }

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = inkOpacity(look.material, look.ink);
  ctx.drawImage(layer, 0, 0);
  ctx.restore();
}

/** Unwrapped band, drawn 1:1 in stage mm. */
export function drawFlat(ctx: CanvasRenderingContext2D, surface: HTMLCanvasElement, C: number, H: number, look: InkLook) {
  ctx.save();
  ctx.globalAlpha = Math.max(0.35, inkOpacity(look.material, look.ink));
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(surface, 0, 0, surface.width, surface.height, 0, 0, C, H);
  ctx.restore();
}
